import React, { useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Box,
  CardMedia,
  CardContent,
  Grid,
  IconButton,
  Divider,
} from "@mui/material";
// import useLocalStorage from "../Components/Hooks/LocalStorage";
import { FETCH_CART } from "../graphql/queries";
import { Add, Remove } from "@mui/icons-material";
import { REMOVE_FROM_CART, UPDATE_CART_ITEMS } from "../graphql/mutations";
import PlaceholderPage from "./MyBiz/PlaceholderPage";
import { toast } from "react-toastify";
import CartSkeleton from "../Components/Skeletons/CartSkeleton";
import Cookies from "js-cookie";

const CartPage: React.FC = () => {
  // const cartId = useLocalStorage("shopifyCartId");
  const cartId = "dummy-cart-id";
  const emptyImage1 = require("../assets/images/Empty1.png");

  const [fetchCart, { data, loading, error }] = useLazyQuery(FETCH_CART);
  const [removeFromCart] = useMutation(REMOVE_FROM_CART);
  const [updateCartItems] = useMutation(UPDATE_CART_ITEMS);

  const [removeCartLoading, setRemoveCartLoading] = React.useState({
    selectedLineId: "",
    loading: false,
  });
  const [quantityLoading, setQuantityLoading] = React.useState(false);

  useEffect(() => {
    fetchCart({ variables: { cartId } });
  }, [cartId, fetchCart]);

  // REMOVE ITEM
  const handleRemoveItem = async (lineId: string, store: string) => {
    Cookies.set("shopDomain", store);
    setRemoveCartLoading({ selectedLineId: lineId, loading: true });
    await removeFromCart({
      variables: { cartId, lineIds: [lineId] },
      refetchQueries: [FETCH_CART],
    });
    setRemoveCartLoading({ selectedLineId: "", loading: false });
  };

  // QUANTITY DECREASE
  const handleDecrementQuantity = async (lineId: string, newQuantity: number, store: string) => {
    if (newQuantity === 0) {
      setQuantityLoading(true);
      handleRemoveItem(lineId, store).then(() => setQuantityLoading(false));
      return;
    }

    setQuantityLoading(true);
    await updateCartItems({
      variables: { cartId, items: { id: lineId, quantity: newQuantity } },
      refetchQueries: [FETCH_CART],
    });
    setQuantityLoading(false);
  };

  // QUANTITY INCREASE
  const handleIncrementQuantity = async (lineId: string, newQuantity: number) => {
    setQuantityLoading(true);
    await updateCartItems({
      variables: { cartId, items: { id: lineId, quantity: newQuantity } },
      refetchQueries: [FETCH_CART],
    });
    setQuantityLoading(false);
  };

  // STORE CHECKOUT
  const handleStoreCheckout = (store: string) => {
    const storeLines = data?.fetchCart.lines.filter(
      (line: any) => line.merchandise.shop_domain === store
    );

    if (!storeLines?.length) {
      toast.error("No items for this store.");
      return;
    }

    const checkoutUrl = storeLines[0].merchandise.checkoutUrl;

    if (!checkoutUrl) {
      toast.error("Checkout URL missing for this store.");
      return;
    }

    localStorage.removeItem("shopifyCartId");
    window.location.href = checkoutUrl;
  };

  const cart = data?.fetchCart;

  // Group lines by store WITHOUT changing layout
  const linesByStore = React.useMemo(() => {
    if (!cart?.lines) return {};
    return cart.lines.reduce((acc: any, line: any) => {
      const store = line.merchandise.shop_domain || "unknown-store";
      if (!acc[store]) acc[store] = [];
      acc[store].push(line);
      return acc;
    }, {});
  }, [cart]);


  if (error) {
    return (<div>Error loading cart: {error?.message}</div>
    );
  }

  if ((!cartId || !cart || cart.lines.length === 0) && !loading) {
    return (
      <PlaceholderPage
        title="Cart"
        emptyStateText="Your cart is empty."
        emptyStateImage={emptyImage1}
        actionButtons={<></>}
      />
    );
  }

  return (
    <Container
      maxWidth="xl"
      id="CartContainer"
      sx={{ backgroundColor: "#f5f5f5", paddingTop: 2, paddingBottom: 2, borderRadius: 16 }}
      style={{ borderRadius: 16, justifyContent: "center", justifyItems: "center" }}
    >
      {quantityLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.3)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: "#e34cb5" }} />
        </Box>
      )}

      <Typography variant="h4" gutterBottom mt={2}>
        Your Cart
      </Typography>

      {loading && <CartSkeleton />}

      {/* ✅ SAME UI layout */}
      {!loading &&
        Object.entries(linesByStore).map(([store, storeLines]: any) => (
          <React.Fragment key={store}>
            {storeLines.map((line: any) => (
              <React.Fragment key={line.id}>
                <Grid
                  container
                  direction="row"
                  spacing={1}
                  maxWidth="md"
                  alignContent="center"
                  alignItems="center"
                >
                  <Grid item xs={2}>
                    <CardMedia
                      component="img"
                      sx={{ width: 150 }}
                      image={line.merchandise.image?.src || ""}
                      alt={line.merchandise.image?.altText || "Product image"}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <CardContent>
                      <Typography variant="subtitle1">
                        {line.merchandise.product.title}
                      </Typography>

                      {line.merchandise.selectedOptions.map(
                        (option: any) => (
                          <Typography key={option.name} variant="overline">
                            {option.name}: {option.value}
                          </Typography>
                        )
                      )}

                      <Button
                        variant="outlined"
                        color="error"
                        sx={{ mt: 1 }}
                        size="small"
                        onClick={() => handleRemoveItem(line.id, line.merchandise.shop_domain)}
                        disabled={removeCartLoading.loading}
                      >
                        {(removeCartLoading.loading && removeCartLoading.selectedLineId === line.id) && (
                          <CircularProgress size={18} sx={{ mr: 2 }} />
                        )}
                        Remove
                      </Button>
                    </CardContent>
                  </Grid>

                  <Grid item xs={3}>
                    <Box display="flex" alignItems="center">
                      <IconButton
                        color="secondary"
                        onClick={() => handleDecrementQuantity(line.id, line.quantity - 1, line.merchandise.shop_domain)}
                      >
                        <Remove />
                      </IconButton>

                      <Typography variant="h6" px={1}>
                        {line.quantity}
                      </Typography>

                      <IconButton
                        color="secondary"
                        onClick={() => handleIncrementQuantity(line.id, line.quantity + 1)}
                      >
                        <Add />
                      </IconButton>
                    </Box>
                  </Grid>

                  <Grid item xs={3}>
                    <Typography variant="body1">
                      ${(line.merchandise.price * line.quantity)?.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ marginTop: "1px", borderWidth: "1px" }} />
              </React.Fragment>
            ))}

            {/* ✅ Store-wise checkout — clean + small + matches old design */}
            <Box textAlign="right" mt={2} mb={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleStoreCheckout(store)}
              >
                Proceed to Checkout – {store}
              </Button>
            </Box>
          </React.Fragment>
        ))}
    </Container>
  );
};

export default CartPage;